import { RefObject, useEffect, useState } from 'react';

export const DEFAULT_DRAWER_MAX_HEIGHT = 264;

export interface DropdownPositionResult {
  openUpward: boolean;
  maxHeight?: number;
}

const CLOSED_RESULT: DropdownPositionResult = { openUpward: false };

/**
 * Hook to determine if dropdown should open upward or downward
 * based on available space in viewport
 * @param isOpen - Whether the dropdown is currently open
 * @param buttonRef - Reference to the select button element
 * @param drawerRef - Reference to the dropdown drawer element
 * @param minSpaceBuffer - Minimum space buffer needed below/above (default: 24px)
 *                        This ensures dropdown doesn't open too close to viewport edge
 */
export const useDropdownPosition = (
  isOpen: boolean,
  buttonRef: RefObject<HTMLElement | null>,
  drawerRef: RefObject<HTMLElement | null>,
  minSpaceBuffer = 24,
  disabled = false,
  fitContentHeight = false
): DropdownPositionResult => {
  const [result, setResult] = useState<DropdownPositionResult>(CLOSED_RESULT);

  useEffect(() => {
    if (!isOpen || !buttonRef?.current || !drawerRef.current) {
      return;
    }

    const button = buttonRef.current;
    const drawer = drawerRef.current;

    const checkPosition = () => {
      if (!button || !drawer) return;

      const buttonRect = button.getBoundingClientRect();
      const drawerHeight = drawer.offsetHeight;
      const viewportHeight = window.innerHeight;

      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;

      const shouldOpenUpward =
        spaceBelow < drawerHeight + minSpaceBuffer && spaceAbove > spaceBelow;

      const maxHeight = fitContentHeight
        ? Math.max(
            DEFAULT_DRAWER_MAX_HEIGHT,
            (shouldOpenUpward ? spaceAbove : spaceBelow) - minSpaceBuffer
          )
        : undefined;

      setResult({ openUpward: shouldOpenUpward, maxHeight });
    };

    // Use requestAnimationFrame to ensure drawer is fully rendered
    // before calculating position
    const rafId = requestAnimationFrame(() => {
      checkPosition();
    });

    const resizeObserver = new ResizeObserver(() => {
      checkPosition();
    });

    if (drawer) {
      resizeObserver.observe(drawer);
    }

    // Recheck on window resize or scroll
    window.addEventListener('resize', checkPosition);
    window.addEventListener('scroll', checkPosition, true);

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkPosition);
      window.removeEventListener('scroll', checkPosition, true);
    };
  }, [isOpen, buttonRef, drawerRef, minSpaceBuffer, fitContentHeight]);

  if (disabled) return CLOSED_RESULT;

  return isOpen ? result : CLOSED_RESULT;
};
